import { useI18n } from "@/context/I18nContext";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { fmtInt, fmtNumber, currencySuffix } from "@/lib/format";
import { OPS } from "@/constants/testIds";

const PI = OPS.posInsights;

function Delta({ pct }) {
    const { t, lang } = useI18n();
    if (pct === null || pct === undefined) return null;
    const up = pct >= 0;
    const Icon = up ? ArrowUpRight : ArrowDownRight;
    return (
        <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold"
            style={{ color: up ? "var(--success)" : "var(--danger)" }} title={t("pi_vs_prev")}>
            <Icon className="h-3 w-3" />
            <bdi dir="ltr">{fmtNumber(Math.abs(pct), lang, 1)}%</bdi>
        </span>
    );
}

function Kpi({ k, label, value, unit, delta, sub }) {
    const { lang } = useI18n();
    const shown = value === null || value === undefined
        ? "\u2014"
        : unit === "int" ? fmtInt(value, lang)
            : unit === "hour" ? `${String(value).padStart(2, "0")}:00`
                : fmtNumber(value, lang, 2);
    return (
        <div data-testid={PI.kpiCard(k)}
            className="rounded-2xl border border-[color:var(--border-soft)] surface px-4 py-3 shadow-[var(--shadow-xs)]">
            <p className="truncate text-xs font-medium text-[color:var(--ink-muted)]">{label}</p>
            <div className="mt-1 flex items-baseline gap-1.5">
                <p className="kpi-number text-xl font-extrabold tnum text-[color:var(--ink)] sm:text-2xl">
                    <bdi dir="ltr">{shown}</bdi>
                </p>
                {unit === "money" ? (
                    <span className="text-[10px] text-[color:var(--ink-muted)]">{currencySuffix(lang)}</span>
                ) : null}
            </div>
            <div className="mt-1 flex items-center gap-2">
                <Delta pct={delta} />
                {sub ? <span className="truncate text-[11px] text-[color:var(--ink-muted)]">{sub}</span> : null}
            </div>
        </div>
    );
}

export default function PosKpiRow({ data }) {
    const { t, lang } = useI18n();
    const tot = data?.totals || {};
    const d = data?.deltas || {};
    const peak = (data?.hourly || []).reduce((m, h) => (!m || h.gross > m.gross ? h : m), null);
    const appsShare = tot.gross ? (tot.apps_total / tot.gross) * 100 : null;

    return (
        <div data-testid={PI.kpi} className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
            <Kpi k="transactions" label={t("pi_kpi_transactions")} value={tot.transactions} unit="int" delta={d.transactions} />
            <Kpi k="avg-ticket" label={t("pi_kpi_avg_ticket")} value={tot.avg_ticket} unit="money" delta={d.avg_ticket} />
            <Kpi k="gross" label={t("pi_kpi_gross")} value={tot.gross} unit="money" delta={d.gross} />
            <Kpi k="apps" label={t("pi_kpi_apps")} value={tot.apps_total} unit="money" delta={d.apps_total}
                sub={appsShare !== null ? `${fmtNumber(appsShare, lang, 1)}%` : null} />
            <Kpi k="discounts" label={t("pi_kpi_discounts")} value={tot.discount_total} unit="money"
                sub={`${fmtInt(tot.discount_orders || 0, lang)} ${t("pi_col_orders")}`} />
            <Kpi k="waste" label={t("pi_kpi_waste")} value={tot.waste_total} unit="money"
                sub={`${fmtInt(tot.waste_orders || 0, lang)} ${t("pi_col_orders")}`} />
            <Kpi k="peak-hour" label={t("pi_peak_hour")} value={peak ? peak.hour : null} unit="hour"
                sub={peak ? `${fmtInt(peak.orders, lang)} ${t("pi_col_orders")}` : null} />
        </div>
    );
}
